import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Check, Sparkles, Loader2, Heart, LogIn } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { startCheckout, fetchUsage, foundingLockInStatus } from "../lib/api";
import { trackAddToCart, trackInitiateCheckout } from "../lib/marketingPixels";
import { toast } from "sonner";
import { useReaderAppMode } from "../lib/readerAppMode";
import { useAuth } from "../context/AuthContext";
import {
  trialPackageFromSearch,
  trialSignupPath,
} from "../lib/trialSignup";

// Source of truth for the marketing copy. Server enforces actual price
// via PACKAGES in billing.py — these values are only for display.
const TIERS = {
  monthly: {
    package_id: "pro_monthly",
    price: 5.99,
    cadenceKey: "pricing.cycleMonthly",
    perMonthLabel: null,
  },
  annual: {
    package_id: "pro_annual",
    price: 49.99,
    cadenceKey: "pricing.cycleYear",
    // 49.99 / 12 ≈ 4.17. Two-decimal anchor — honesty + clarity.
    perMonthLabel: "$4.17/mo",
  },
  lifetime: {
    package_id: "founding_lifetime",
    price: 100,
    cadenceKey: "pricing.cycleOnce",
    perMonthLabel: null,
  },
};

const SAVINGS_PCT = Math.round(
  (1 - TIERS.annual.price / (TIERS.monthly.price * 12)) * 100,
);

export const PricingCards = ({
  currentTier = "free",
  showFreeCta = true,
  onUpgrade,
  requirePlanChoice = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cycle, setCycle] = useState(
    requirePlanChoice ? null : "annual",
  );
  const [foundingStatus, setFoundingStatus] = useState(null);
  const tier = TIERS[cycle];
  const readerApp = useReaderAppMode();

  // ─── Guest gate ────────────────────────────────────────────────────
  // Guest users (auth_provider="guest", synthetic
  // guest_xxx@guest.trubeaute.local email) have no way to manage a
  // recurring subscription later — no login, no email to reach. We
  // route them to /login FIRST so they create a real account, then
  // come back to subscribe. Prevents the "anonymous paying customer
  // the founder can't reach" failure mode.
  const isGuest = !!(
    user && (user.email || "").toLowerCase().endsWith("@guest.trubeaute.local")
  );
  const needsAccount = !user || isGuest;

  // Resume the exact plan chosen before signup/sign-in. The previous flow
  // used React Router state, but Login only honors a sanitized `next=` query
  // parameter, so new members lost their plan and landed in the app without
  // ever reaching Stripe. Remove the marker before starting Checkout so a
  // browser back/reload cannot create duplicate sessions.
  useEffect(() => {
    if (!user || isGuest || readerApp) return;
    const params = new URLSearchParams(window.location.search);
    const packageId = trialPackageFromSearch(window.location.search);
    if (!packageId) return;

    const nextCycle = packageId === "pro_monthly" ? "monthly" : "annual";
    setCycle(nextCycle);
    params.delete("checkout");
    const cleanSearch = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`,
    );

    let cancelled = false;
    setLoading(true);
    startCheckout(packageId)
      .then(({ url }) => {
        if (cancelled) return;
        trackInitiateCheckout({
          content_id: packageId,
          value: TIERS[nextCycle].price,
          currency: "USD",
        });
        window.location.href = url;
      })
      .catch((error) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[checkout] post-auth resume failed:", error);
        toast.error(t("pricing.checkoutError"));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, isGuest, readerApp, t]);

  // Feature lists pulled from i18n so they render in the user's locale.
  const featuresFree = [0, 1, 2, 3, 4].map((i) => t(`pricing.featuresFree.${i}`));
  const featuresPro = [0, 1, 2, 3, 4].map((i) => {
    const feature = t(`pricing.featuresPro.${i}`);
    // Drag tutorials are not currently offered. Strip the stale
    // parenthetical from every locale until that library exists.
    return i === 1 ? feature.replace(/\s*\([^)]*drag[^)]*\)/i, "") : feature;
  });

  // ─── 7-day trial eligibility ────────────────────────────────────
  // Backend /me/usage tells us whether THIS user qualifies for the
  // first-time-Pro free trial. We display "Try free for 7 days" on
  // the primary CTA when eligible; otherwise the regular price.
  // Trial-eligible users see a soft banner explaining the trial.
  const [trialEligible, setTrialEligible] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const usage = await fetchUsage();
        if (!cancelled) setTrialEligible(!!usage?.trial_eligible);
      } catch {
        // Silent — defaults to no trial CTA, which is the safer
        // copy. Users who SHOULD see the trial just see the regular
        // price; never the inverse (where we'd promise a trial that
        // doesn't exist).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.user_id]);

  useEffect(() => {
    if (readerApp) return;
    foundingLockInStatus().then(setFoundingStatus).catch(() => {});
  }, [readerApp]);

  // ─── Apple "Reader app" mode (iOS Capacitor build) ─────────────────
  // Apple's App Store Review Guidelines 3.1.3(a) / 3.1.3(b) prohibit
  // apps from mentioning, showing, or linking to external purchase
  // flows for digital content. When we detect we're running inside
  // the iOS shell, we swap the pricing cards for a neutral
  // "sign in to access your features" panel that carries NO prices,
  // NO "upgrade" / "subscribe" language, and NO web links.
  if (readerApp) {
    return (
      <div
        className="rounded-3xl border border-[#EADDD7] bg-white/80 backdrop-blur-xl p-8 text-center soft-shadow"
        data-testid="reader-app-pricing-replacement"
      >
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#F4DCD3] text-[#9B6A6C] mb-4">
          <Heart className="h-6 w-6" />
        </div>
        <h3 className="text-2xl font-light text-[#2C2123] mb-2">
          {t("pricing.readerHeading")}
        </h3>
        <p className="text-[#5C4B51] max-w-md mx-auto mb-6 leading-relaxed">
          {t("pricing.readerBody")}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-full bg-[#9B6A6C] text-white px-6 py-3 hover:bg-[#805557] transition"
          data-testid="reader-app-signin-btn"
        >
          <LogIn className="h-4 w-4" />
          {t("pricing.readerSignIn")}
        </Link>
      </div>
    );
  }

  const upgrade = async () => {
    if (loading) return;
    if (user && user.email_verified === false) {
      toast.info(t("pricing.verifyEmailCheckout"));
      return;
    }
    if (!tier) {
      toast.info("Choose Monthly, Annual, or Lifetime to continue.");
      return;
    }
    // TikTok/Taboola/GTM: user clicked Subscribe — fires AddToCart
    // BEFORE any guest-redirect or async work so it always lands
    // even if Stripe / api errors out. Plan + value let TikTok
    // optimize for higher-LTV signups.
    trackAddToCart({
      content_id: tier.package_id,
      content_name: cycle === "lifetime"
        ? "Founding Lifetime Pro"
        : tier.package_id === "pro_annual"
          ? "Pro · Annual"
          : "Pro · Monthly",
      content_type: cycle === "lifetime" ? "lifetime_access" : "subscription",
      value: tier.price,
      currency: "USD",
      quantity: 1,
    });
    if (cycle === "lifetime") {
      if (needsAccount) {
        navigate("/login?signup=1&next=%2Flock-in");
        return;
      }
      navigate("/lock-in");
      return;
    }
    // Guest users need a real account before they can subscribe.
    if (needsAccount) {
      toast.info(
        "Create your account to start your 7-day trial. You'll add a card in Stripe and can cancel before the first charge.",
      );
      navigate(trialSignupPath(tier.package_id));
      return;
    }
    setLoading(true);
    try {
      const { url } = await startCheckout(tier.package_id);
      // TikTok/Taboola/GTM: about to redirect to Stripe — fires
      // InitiateCheckout. We push this BEFORE the redirect so the
      // beacon has time to leave the page.
      trackInitiateCheckout({
        content_id: tier.package_id,
        value: tier.price,
        currency: "USD",
      });
      if (onUpgrade) onUpgrade();
      window.location.href = url;
    } catch (e) {
      // Build the most useful error message we can from whatever the
      // backend / network gave us. Order:
      //   1. detail string from FastAPI HTTPException
      //   2. detail array (FastAPI 422 body) — join the messages
      //   3. message from a network failure
      //   4. status code only
      //   5. generic fallback
      let msg = t("pricing.checkoutError");
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail) {
        msg = detail;
      } else if (Array.isArray(detail)) {
        const joined = detail
          .map((d) => (typeof d === "string" ? d : d?.msg))
          .filter(Boolean)
          .join("; ");
        if (joined) msg = joined;
      } else if (e?.message) {
        msg = `${e.message}${status ? ` (HTTP ${status})` : ""}`;
      } else if (status) {
        msg = `Checkout failed with HTTP ${status}.`;
      }
      // eslint-disable-next-line no-console
      console.error("[checkout] failed:", { status, detail, raw: e });
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div
      className={requirePlanChoice
        ? "max-w-xl mx-auto"
        : "grid sm:grid-cols-2 gap-5"}
      data-testid="pricing-cards"
    >
      {/* Free */}
      {!requirePlanChoice && <div
        className="rounded-3xl border border-[#EADDD7] bg-white/80 backdrop-blur-xl p-7 soft-shadow flex flex-col"
        data-testid="plan-free"
      >
        <div className="flex items-center gap-2 mb-1">
          <Heart className="h-4 w-4 text-[#9B6A6C]" />
          <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C]">
            {t("pricing.free")}
          </span>
        </div>
        <h3 className="text-3xl font-light text-[#2C2123] mb-1">
          {t("pricing.freeTitle")}
        </h3>
        <p className="text-[#5C4B51] mb-5">{t("pricing.freeTagline")}</p>
        <div className="mb-6">
          <span className="text-4xl font-light text-[#2C2123]">$0</span>
          <span className="text-[#8A7D82] ml-1">{t("pricing.perMonth")}</span>
        </div>
        <ul className="space-y-3 mb-7">
          {featuresFree.map((f) => (
            <li
              key={f}
              className="flex gap-3 items-start text-[#3C2F33]"
            >
              <Check className="h-4 w-4 text-[#9B6A6C] flex-shrink-0 mt-1" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto">
          {currentTier === "free" ? (
            <div
              className="text-center w-full rounded-full border border-[#D5A8A4] text-[#5D4050] py-3 text-sm bg-[#F8EEEE]"
              data-testid="free-current-badge"
            >
              {t("pricing.currentPlan")}
            </div>
          ) : (
            showFreeCta && (
              <div className="text-center w-full text-[#8A7D82] text-sm">
                {t("pricing.alwaysFree")}
              </div>
            )
          )}
        </div>
      </div>}

      {/* Pro */}
      <div
        className="relative rounded-3xl border-2 border-[#9B6A6C] bg-gradient-to-br from-[#F4DCD3] via-[#F8E5DD] to-[#FAEDE6] p-7 shadow-[0_24px_60px_-20px_rgba(155,106,108,0.45)] flex flex-col"
        data-testid="plan-pro"
      >
        <div
          className="inline-flex self-start bg-[#9B6A6C] text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full mb-4"
          data-testid="plan-pro-recommended-badge"
        >
          {t("pricing.recommended")}
        </div>

        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#9B6A6C]" />
            <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C]">
              {t("pricing.pro")}
            </span>
          </div>
          {/* Cycle toggle */}
          <div
            className="inline-flex bg-white/60 backdrop-blur rounded-full p-0.5 border border-[#E5C5C2]"
            role="tablist"
            aria-label="Billing cycle"
            data-testid="pro-cycle-toggle"
          >
            <button
              onClick={() => setCycle("monthly")}
              role="tab"
              aria-selected={cycle === "monthly"}
              className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full transition-colors ${
                cycle === "monthly"
                  ? "bg-[#9B6A6C] text-white"
                  : "text-[#9B6A6C] hover:text-[#5D4050]"
              }`}
              data-testid="pro-cycle-monthly"
            >
              {t("pricing.cycleMonthly")}
            </button>
            <button
              onClick={() => setCycle("annual")}
              role="tab"
              aria-selected={cycle === "annual"}
              className={`relative text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full transition-colors ${
                cycle === "annual"
                  ? "bg-[#9B6A6C] text-white"
                  : "text-[#9B6A6C] hover:text-[#5D4050]"
              }`}
              data-testid="pro-cycle-annual"
            >
              {t("pricing.cycleAnnual")}
            </button>
            {foundingStatus?.is_full !== true && (
              <button
                onClick={() => setCycle("lifetime")}
                role="tab"
                aria-selected={cycle === "lifetime"}
                className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full transition-colors ${
                  cycle === "lifetime"
                    ? "bg-[#9B6A6C] text-white"
                    : "text-[#9B6A6C] hover:text-[#5D4050]"
                }`}
                data-testid="pro-cycle-lifetime"
              >
                {t("pricing.cycleLifetime", { defaultValue: "Lifetime" })}
              </button>
            )}
          </div>
        </div>

        <h3 className="text-3xl font-light text-[#2C2123] mb-1">
          {t("pricing.proTitle")}
        </h3>
        <p className="text-[#5C4B51] mb-5">{t("pricing.proTagline")}</p>
        <div className="mb-6">
          {tier ? <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="text-4xl font-light text-[#2C2123]"
              data-testid="pro-price"
            >
              ${tier.price}
            </span>
            <span className="text-[#8A7D82]">
              {t(tier.cadenceKey, {
                defaultValue: cycle === "lifetime" ? "one time" : undefined,
              })}
            </span>
            {cycle === "annual" && (
              <span
                className="text-xs uppercase tracking-[0.2em] bg-[#9B6A6C] text-white px-2 py-1 rounded-full"
                data-testid="pro-savings-badge"
              >
                {t("pricing.save", { pct: SAVINGS_PCT })}
              </span>
            )}
            {cycle === "lifetime" && foundingStatus && (
              <span className="text-xs text-[#5D4050]" data-testid="founding-spots-remaining">
                {t("pricing.foundingKicker", {
                  remaining: foundingStatus.remaining,
                  defaultValue: `The Founding 100 · ${foundingStatus.remaining} spots remain`,
                })}
              </span>
            )}
          </div> : (
            <div
              className="rounded-2xl border border-[#D5A8A4] bg-white/70 px-4 py-3 text-sm text-[#5D4050]"
              data-testid="pro-cycle-required-message"
            >
              {t("pricing.trialChoiceRequired")}
            </div>
          )}
          {tier?.perMonthLabel && (
            <div
              className="text-xs text-[#5C4B51] mt-1"
              data-testid="pricing-per-month-hint"
            >
              {/* Controlled by translation file. `<Trans>` keeps the
                  `<b>` emphasis without `dangerouslySetInnerHTML`. */}
              <Trans
                i18nKey="pricing.perMonthHint"
                values={{ price: tier.perMonthLabel }}
                components={{ b: <strong /> }}
              />
            </div>
          )}
          {/* 7-day trial banner — only renders when this user is
              eligible (first-time Pro buyers). Soft, friendly, makes
              the commitment feel low-stakes. Card-on-file is still
              required (Stripe's spec), which we say plainly. */}
          {trialEligible && !isGuest && cycle && cycle !== "lifetime" && (
            <div
              className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5"
              data-testid="pricing-trial-banner"
            >
              <p className="text-[12.5px] text-emerald-900 leading-snug">
                <strong className="font-semibold">
                  Try free for 7 days.
                </strong>{" "}
                <span className="text-emerald-800/85">
                  Cancel anytime. Card required, no charge until day 8.
                </span>
              </p>
            </div>
          )}
          {/* Beta lock-in pill — only on the web build (readerApp
              already swaps the whole pricing card for a sign-in panel
              on iOS, so this branch never renders there). Honestly
              anchors the deal without making a hard promise. */}
          <div
            className="mt-3 inline-flex items-center gap-1.5 bg-[#9B6A6C]/15 text-[#5D4050] rounded-full px-3 py-1.5 text-[11px] font-medium"
            data-testid="pricing-beta-lock-pill"
          >
            <Sparkles className="h-3 w-3" />
            {t("pricing.betaLockIn")}
          </div>
        </div>
        <ul className="space-y-3 mb-7">
          {featuresPro.map((f) => (
            <li
              key={f}
              className="flex gap-3 items-start text-[#3C2F33]"
            >
              <Check className="h-4 w-4 text-[#9B6A6C] flex-shrink-0 mt-1" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto">
          {currentTier === "pro" ? (
            <button
              onClick={upgrade}
              disabled={loading || !tier}
              className="w-full rounded-full bg-[#9B6A6C] text-white py-3 hover:bg-[#805557] transition flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="extend-pro-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {cycle === "annual"
                ? t("pricing.extendAnnual")
                : cycle === "lifetime"
                  ? t("pricing.foundingCta", { defaultValue: "Choose lifetime access" })
                  : t("pricing.extendMonthly")}
            </button>
          ) : (
            <button
              onClick={upgrade}
              disabled={loading || !tier}
              className="w-full rounded-full bg-[#9B6A6C] text-white py-3 hover:bg-[#805557] transition flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_6px_20px_rgba(155,106,108,0.35)]"
              data-testid="upgrade-pro-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {!tier
                ? "Choose Monthly, Annual, or Lifetime"
                : cycle === "lifetime"
                ? t("pricing.foundingCta", { defaultValue: "Choose lifetime access" })
                : trialEligible && !isGuest
                ? "Start 7-day free trial"
                : t("pricing.upgrade")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCards;
